import {Component, computed, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_APP} from "@app/common/services/logging"
import {FormsModule} from "@angular/forms"
import {MatDialogRef} from "@angular/material/dialog"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatOptionModule} from "@angular/material/core"
import {MatSelectModule} from "@angular/material/select"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {AddItemDialogComponent} from "@common/components/dialogs/add-item-dialog/add-item-dialog.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {MembershipRole, MutationInviteUserInput} from "@generated"
import {InviteUserGQL} from "@platform/components/users/users-grid/users-grid.generated"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {extractErrorInfo} from "@common/helpers/api/errors"
import {z} from "zod"
import {InputContainerComponent} from "@app/common/components/inputs/input-container/input-container.component"

// The invited user supplies their own name when completing registration (the "welcome" flow), so we
// only collect the email and role here.
const UserDataSchema = z.object({
    email: z.string().min(1, "Email is required").email("Please enter a valid email address").max(100, "Email must be less than 100 characters"),
    role: z.nativeEnum(MembershipRole, {
        errorMap: () => ({message: "Please select a valid role"}),
    }),
})

type UserData = z.infer<typeof UserDataSchema>

type ValidationErrors = {
    [K in keyof UserData]?: string
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-user-dialog",
    templateUrl: "./add-user-dialog.component.html",
    styleUrls: ["./add-user-dialog.component.scss"],
    imports: [
        FormsModule,
        InputContainerComponent,
        AddItemDialogComponent,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        StringInputComponent,
        OrganizationSelectComponent,
    ],
})
export class AddUserDialogComponent implements OnInit {
    private readonly logger = inject(CM_LOGGER_APP)
    private dialogRef = inject(MatDialogRef<AddUserDialogComponent>)
    private inviteUserGQL = inject(InviteUserGQL)
    private readonly organizations = inject(OrganizationsService)
    protected readonly permissions = inject(PermissionsService)

    readonly $membershipRoles = signal(Object.keys(MembershipRole))
    readonly $isLoading = signal(false)
    readonly $canAddUserToAnyOrganization = computed(() => this.permissions.$to().create.user())

    readonly $userData = signal<UserData>({
        email: "",
        role: MembershipRole.Editor,
    })

    readonly $organizationId = signal<string | undefined>(undefined)
    readonly $errors = signal<ValidationErrors>({})
    hasValidated = false

    ngOnInit() {
        // Set default organization if not already set (for non-staff users or when opened without context)
        if (!this.$organizationId()) {
            this.$organizationId.set(this.organizations.$current()?.id)
        }
    }

    updateOrganization = async (data: {organizationId: string}) => {
        this.$organizationId.set(data.organizationId)
    }

    validateField(fieldName: keyof UserData) {
        try {
            const fieldSchema = UserDataSchema.shape[fieldName]
            fieldSchema.parse(this.$userData()[fieldName])
            delete this.$errors()[fieldName]
        } catch (error) {
            if (error instanceof z.ZodError) {
                this.$errors()[fieldName] = error.errors[0]?.message || "Invalid value"
            }
        }
    }

    validateAll(): boolean {
        this.hasValidated = true
        try {
            UserDataSchema.parse(this.$userData())
            this.$errors.set({})
            return true
        } catch (error) {
            if (error instanceof z.ZodError) {
                this.$errors.set({})
                error.errors.forEach((err) => {
                    const fieldName = err.path[0] as keyof UserData
                    this.$errors()[fieldName] = err.message
                })
            }
            return false
        }
    }

    onFieldChange(fieldName: keyof UserData) {
        if (this.hasValidated || this.$errors()[fieldName]) {
            this.validateField(fieldName)
        }
    }

    onEmailChange(value: string) {
        this.$userData.update((current) => ({...current, email: value}))
        this.onFieldChange("email")
    }

    onRoleChange(value: MembershipRole) {
        this.$userData.update((current) => ({...current, role: value}))
        this.onFieldChange("role")
    }

    readonly $isFormValid = computed(() => Object.keys(this.$errors()).length === 0 && !!this.$userData().email && !!this.$userData().role)

    async onConfirm() {
        if (!this.validateAll()) {
            return
        }

        const organizationId = this.$organizationId()
        if (!organizationId) {
            this.logger.error("No organization ID provided")
            return
        }

        this.$isLoading.set(true)
        try {
            const input: MutationInviteUserInput = {
                email: this.$userData().email,
                memberships: [
                    {
                        organizationId,
                        role: this.$userData().role,
                    },
                ],
            }

            const result = await mutateThrowingErrors(this.inviteUserGQL)({input})
            this.dialogRef.close(result.inviteUser)
        } catch (error) {
            this.logger.error("Failed to invite user:", error)
            const errorInfo = extractErrorInfo(error, {409: "User with this email already exists"})
            alert(`Failed to invite user: ${errorInfo.message}`)
        } finally {
            this.$isLoading.set(false)
        }
    }
}
